import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { IconButton, TextField } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/formatNumber";

const StyledInput = styled((props: any) => <TextField variant="standard" size="small" type="text" {...props} />)({
    width: 120,
    '& .MuiInputBase-input': {
        padding: '6px 8px',
    },
});

interface AmountInputProps {
    value: number;
    handleInputChange: (value: number) => void;
    handleSubmit: (value: number) => void;
}

const AmountInput = ({ value, handleInputChange, handleSubmit }: AmountInputProps) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const originalValueRef = useRef<number>(value);

    const { t } = useTranslation("common");

    const cancelEdit = useCallback(() => {
        handleInputChange(originalValueRef.current);
        setIsEditMode(false);
    }, [handleInputChange]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && isEditMode) {
            handleSubmit(value);
            setIsEditMode(false);
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    const handleClickAway = useCallback((e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
            cancelEdit();
        }
    }, [cancelEdit]);

    useEffect(() => {
        if (isEditMode) {
            document.addEventListener('mousedown', handleClickAway);
            return () => {
                document.removeEventListener('mousedown', handleClickAway);
            };
        }
    }, [isEditMode, handleClickAway]);

    const handleButtonClick = (value: number) => {
        if (isEditMode) {
            handleSubmit(value);
            setIsEditMode(false);
        } else {
            originalValueRef.current = value;
            setIsEditMode(true);
            // this needs to be done to focus the input after it becomes enabled
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }

    return (
        <div ref={containerRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <StyledInput
                value={isEditMode ? (!!value ? value : '') : formatCurrency(value)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(Number(e.target.value))}
                onKeyDown={handleKeyDown}
                disabled={!isEditMode}
                inputRef={inputRef}
            />
            <IconButton
                aria-label={t("reports.common.save")}
                size="small"
                onClick={() => handleButtonClick(value)}
            >
                {isEditMode ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            </IconButton>
        </div>
    );
};

export default AmountInput;
